import type { BookingAnswerValue } from '@shared/core/bookings'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  BookingDateAndTimeStep,
  BookingDetailsStep,
  BookingDisabled,
  BookingStepHeader,
  PoweredBy,
} from 'src/modules/booking/components'
import { useAppointmentTypes, useProviderDetails } from 'src/modules/booking/hooks'
import { useNoReferrerPolicy } from 'src/modules/booking/hooks/use-no-referrer-policy'
import { Loader } from 'src/modules/core/components'
import { ApiService } from 'src/services/api.service'
import { pinBookingForm } from './booking-wrapper.domain'
import classes from './booking-wrapper.module.scss'

import type { BookingData, BookingStep } from './booking-wrapper.interface'

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_referrer'] as const

export function BookingWrapper() {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const [currentStep, setCurrentStep] = useState<BookingStep>('date-time')
  const [bookingData, setBookingData] = useState<BookingData>({})
  const embedTrackedRef = useRef(false)
  useNoReferrerPolicy()

  useEffect(() => {
    if (embedTrackedRef.current || !params.providerId) return
    const utmProps: Record<string, string> = {}
    for (const key of UTM_KEYS) {
      const value = searchParams.get(key)
      if (value) utmProps[key] = value
    }
    if (Object.keys(utmProps).length === 0) return
    embedTrackedRef.current = true
    void ApiService.BookingAnalytics.trackBookingEmbedClicked(params.providerId, utmProps)
  }, [params.providerId, searchParams])

  // Fetch appointment types and provider details
  const { data: appointmentTypes = [], isLoading: isLoadingAppointmentTypes } = useAppointmentTypes(
    params.providerId || '',
    { enabled: !!params.providerId },
  )
  const { data: providerDetails, isLoading: isLoadingProviderDetails } = useProviderDetails(params.providerId || '', {
    enabled: !!params.providerId,
  })
  const defaultOrFirstAppointmentTypeId = useMemo(
    () => appointmentTypes.find((type) => type.is_default)?.id ?? appointmentTypes[0]?.id,
    [appointmentTypes],
  )

  useEffect(() => {
    if (!defaultOrFirstAppointmentTypeId) return
    setBookingData((prev) =>
      prev.appointmentType ? prev : { ...prev, appointmentType: defaultOrFirstAppointmentTypeId },
    )
  }, [defaultOrFirstAppointmentTypeId])

  const handleDateTimeNext = (data: Partial<BookingData>) => {
    setBookingData((prev) => pinBookingForm({ ...prev, ...data }, appointmentTypes))
    setCurrentStep('details')
  }

  const handleAnswerChange = useCallback((questionId: string, value: BookingAnswerValue) => {
    setBookingData((prev) => ({ ...prev, answers: { ...prev.answers, [questionId]: value } }))
  }, [])

  const handleBack = () => {
    if (currentStep === 'details') {
      setCurrentStep('date-time')
    }
  }

  const isLoading = isLoadingAppointmentTypes || isLoadingProviderDetails
  const providerName = providerDetails?.full_name || 'Provider'
  const logoUrl = providerDetails?.logo_url

  // Show loader while fetching data
  if (isLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.root}>
          <Loader isLoading={true} full />
        </div>
        <PoweredBy className={classes.poweredBy} />
      </div>
    )
  }

  // Show disabled message if no appointment types
  if (appointmentTypes.length === 0) {
    return (
      <div className={classes.container}>
        <div className={classes.root}>
          <BookingDisabled providerName={providerName} logoUrl={logoUrl} />
        </div>
        <PoweredBy className={classes.poweredBy} />
      </div>
    )
  }

  // Normal booking flow
  return (
    <div className={classes.container}>
      <div className={classes.root}>
        <BookingStepHeader
          providerName={providerName}
          logoUrl={logoUrl}
          activeStep={currentStep === 'date-time' ? 1 : 2}
        />
        {currentStep === 'date-time' && (
          <BookingDateAndTimeStep
            providerId={params.providerId}
            slug={params.slug}
            onNext={handleDateTimeNext}
            initialData={bookingData}
          />
        )}
        {currentStep === 'details' && (
          <BookingDetailsStep
            providerId={params.providerId}
            slug={params.slug}
            onNext={handleDateTimeNext}
            onBack={handleBack}
            onAnswerChange={handleAnswerChange}
            initialData={bookingData}
          />
        )}
      </div>
      <PoweredBy className={classes.poweredBy} />
    </div>
  )
}
